import { useState, useEffect } from "react";
import { XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";
import { motion } from "framer-motion";
import GlassCard from "./GlassCard";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { tierMeta } from "@/lib/tiers";


function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-card/90 backdrop-blur-lg border border-white/10 rounded-xl px-4 py-3 shadow-xl">
      <p className="font-mono text-lg font-bold text-text-primary">{d.score}/900</p>
      <p className="text-xs text-text-secondary">{d.tier} &middot; {formatDate(d.created_at)}</p>
      <p className="text-xs text-text-secondary mt-1">Confidence: {Math.round((d.confidence || 0) * 100)}%</p>
    </div>
  );
}

export default function ScoreTrendChart() {
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // AuthContext exposes `user`, never `token`. This component used to read
  // `token` and bail out, so the whole Score Trend section was dead on arrival.
  // Auth now rides on the shared api client (Bearer header + cookie).
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    api.get("/api/score/trend", { timeout: 10000 })
      .then(({ data }) => {
        if (cancelled) return;
        setTrend(Array.isArray(data?.trend) ? data.trend : []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
        setError(true);
      });

    return () => { cancelled = true; };
  }, [user]);

  if (loading) {
    return (
      <GlassCard className="p-6">
        <div className="h-32 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-blue-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </GlassCard>
    );
  }

  // Graceful degradation: signed-out visitors and transient failures show nothing
  // rather than an error card, since the trend is supplementary to the score.
  if (error || !user) {
    return null;
  }

  if (trend.length < 2) {
    return (
      <GlassCard className="p-6">
        <h3 className="font-heading text-lg font-semibold text-text-primary mb-2">Score Trend</h3>
        <p className="text-sm text-text-secondary">
          Generate your score at least twice to see your trend chart. Each score calculation adds a data point.
        </p>
      </GlassCard>
    );
  }

  const latestScore = trend[trend.length - 1]?.score || 0;
  const firstScore = trend[0]?.score || 0;
  const delta = latestScore - firstScore;
  const deltaColor = delta >= 0 ? "#10b981" : "#ef4444";
  const tierColor = tierMeta(trend[trend.length - 1]?.tier).color;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <GlassCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-text-primary">Score Trend</h3>
          <div className="flex items-center gap-2">
            <span className="font-mono text-2xl font-bold text-text-primary">{latestScore}</span>
            <span
              className="font-mono text-sm font-semibold px-2 py-0.5 rounded-full"
              style={{ color: deltaColor, backgroundColor: `${deltaColor}20` }}
            >
              {delta >= 0 ? "+" : ""}{delta}
            </span>
          </div>
        </div>

        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trend} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <defs>
                <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={tierColor} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={tierColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="created_at"
                tickFormatter={formatDate}
                tick={{ fill: "#94a3b8", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={[300, 900]}
                tick={{ fill: "#94a3b8", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={35}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="score"
                stroke={tierColor}
                strokeWidth={2.5}
                fill="url(#scoreGradient)"
                animationDuration={1500}
                dot={{ r: 4, fill: tierColor, strokeWidth: 2, stroke: "#0a0f1e" }}
                activeDot={{ r: 6, fill: tierColor, strokeWidth: 2, stroke: "#fff" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-text-secondary">{trend.length} score calculations</span>
          <span className="text-xs text-text-secondary">
            {formatDate(trend[0]?.created_at)} → {formatDate(trend[trend.length - 1]?.created_at)}
          </span>
        </div>
      </GlassCard>
    </motion.div>
  );
}
